import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AvEmptyStateComponent } from '../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvPaginatorComponent } from '../../../../Shared/Components/av-paginator/av-paginator.component';
import { AvStatusChipComponent } from '../../../../Shared/Components/av-status-chip/av-status-chip.component';
import { DocumentReasonDialogComponent } from '../../Dialogs/document-reason-dialog/document-reason-dialog.component';
import { RefundDialogComponent } from '../../Dialogs/refund-dialog/refund-dialog.component';
import { BillingDocumentsFacade } from '../../../../../Facades/Billing/billing-documents.facade';
import { DATE_TIME_FORMAT, VALUE_UNRESOLVED } from '../../../../../Consts/backoffice.const';
import {
  CONFIRM_BUTTON,
  PAYMENT_RETRY_CONSEQUENCE,
  PAYMENT_RETRY_TITLE,
} from '../../../../../Consts/billing-documents.const';
import { PAYMENT_STATUS_LABEL, PAYMENT_STATUS_OPTIONS } from '../../../../../Enums/stripe-status.enum';
import { PaymentSummary } from '../../../../../Models/backoffice/payment-summary.model';
import {
  DocumentReasonDialogResult,
  RefundDialogResult,
} from '../../../Interfaces/billing-dialog.interface';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'av-payment-table',
  standalone: true,
  imports: [
    DatePipe,
    DecimalPipe,
    MatIconModule,
    MatTooltipModule,
    AvEmptyStateComponent,
    AvPaginatorComponent,
    AvStatusChipComponent,
    TranslateModule,
  ],
  templateUrl: './payment-table.component.html',
  styleUrl: './payment-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class PaymentTableComponent {
  private readonly _dialog = inject(MatDialog);

  readonly facade = inject(BillingDocumentsFacade);

  readonly dateTimeFormat = DATE_TIME_FORMAT;
  readonly unresolved = VALUE_UNRESOLVED;
  readonly statusOptions = PAYMENT_STATUS_OPTIONS;
  readonly statusLabelKeys = PAYMENT_STATUS_LABEL;

  readonly isEmpty = computed<boolean>(
    () => this.facade.paymentsState() === 'ready' && this.facade.payments().length === 0,
  );

  filterStatus(value: string): void {
    this.facade.filterPayments(value);
  }

  goToPage(page: number): void {
    this.facade.goToPaymentPage(page);
  }

  changePageSize(pageSize: number): void {
    this.facade.changePageSize(pageSize);
  }

  openRetry(payment: PaymentSummary): void {
    const reference = this._dialog.open(DocumentReasonDialogComponent, {
      data: {
        title: PAYMENT_RETRY_TITLE,
        consequence: PAYMENT_RETRY_CONSEQUENCE,
        organizationName: this.facade.organizationName(),
        subjectLabel: payment.stripe_payment_intent_id ?? payment.id,
        confirmLabel: CONFIRM_BUTTON,
        isReasonRequired: true,
      },
    });

    reference
      .afterClosed()
      .subscribe((result?: DocumentReasonDialogResult) => this._retry(payment.id, result));
  }

  openRefund(payment: PaymentSummary): void {
    const reference = this._dialog.open(RefundDialogComponent, {
      data: {
        organizationName: this.facade.organizationName(),
        paymentReference: payment.stripe_payment_intent_id ?? payment.id,
        currency: payment.currency,
        refundableRemainder: payment.refundable_remainder,
      },
    });

    reference
      .afterClosed()
      .subscribe((result?: RefundDialogResult) => this._refund(payment.id, result));
  }

  private _retry(paymentId: string, result?: DocumentReasonDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.retryPayment(paymentId, result.reason);
  }

  private _refund(paymentId: string, result?: RefundDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.refundPayment(paymentId, {
      amount: result.amount ?? undefined,
      reason: result.reason,
      note: result.note ?? undefined,
    });
  }
}
